/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import type { TeamSupportSeats } from '@/services/requests/housien-services/AgenciesService.team';

/**
 * How many auxiliary users the team holds, and how many it may still take.
 *
 * A meter of its own, beside the paid seats and never inside them. The two
 * numbers answer different questions and adding them would answer neither:
 * a team can be out of paid seats with room for a receptionist, or full of
 * receptionists with a broker seat going spare.
 *
 * The figure follows the subscription, so the note says so rather than
 * offering a button. There is nothing to buy here — more paid seats is the
 * only way to get more, and that decision lives in the block above.
 *
 * Renders nothing when the plan grants none, which covers solo and any
 * subscription whose catalogue predates auxiliary users.
 */
export const SupportSeatsMeter: React.FC<{
  supportSeats: TeamSupportSeats | null | undefined;
  /** Per-tier figures, for the explanation of where the total came from. */
  tiers?: Array<{ planId: string; title: string; seats: number; perSeat: number }>;
  /** Opens the seats block above, which is the only way to get more. */
  onAddSeat?: () => void;
}> = ({ supportSeats, tiers = [], onAddSeat }) => {
  const { t } = useTranslation('features/subscription');
  if (!supportSeats || supportSeats.total <= 0) return null;

  const { total, used } = supportSeats;
  const share = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;

  return (
    <div
      data-testid="plan-support-seats"
      style={{
        background: 'var(--surface-card)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4) var(--space-5)',
        display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <Text component="span" style={{ fontWeight: 'var(--font-bold)' }}>
          {t('supportSeats.title')}
        </Text>
        <Text
          component="span"
          data-testid="plan-support-seats-count"
          style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}
        >
          {t('supportSeats.occupied', { used, total })}
        </Text>
      </div>
      <div
        aria-hidden
        style={{
          height: 6, borderRadius: 'var(--radius-full)',
          background: 'var(--surface-secondary)', overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${share}%`, height: '100%',
            background: 'var(--seed-base)',
          }}
        />
      </div>
      <Text
        component="span"
        style={{ fontSize: 'var(--text-tag)', color: 'var(--text-tertiary)' }}
      >
        {t('supportSeats.note')}
      </Text>

      {/* Where the total came from, tier by tier. A single number invites the
          question "why that many?", and the answer is arithmetic the reader
          can redo: six paid users times ten. */}
      {tiers.length > 0 ? (
        <div
          data-testid="plan-support-seats-breakdown"
          style={{
            display: 'flex', flexDirection: 'column', gap: 'var(--space-1)',
            paddingTop: 'var(--space-2)',
            borderTop: '1px solid var(--border-ultra-subtle)',
          }}
        >
          <Text component="span" style={{ fontSize: 'var(--text-tag)', fontWeight: 'var(--font-bold)' }}>
            {t('supportSeats.howTitle')}
          </Text>
          {tiers.map((tier) => (
            <div
              key={tier.planId}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-2)' }}
            >
              <Text component="span" style={{ fontSize: 'var(--text-tag)', color: 'var(--text-secondary)' }}>
                {`${tier.seats} ${tier.title} × ${tier.perSeat}`}
              </Text>
              <Text component="span" style={{ fontSize: 'var(--text-tag)', color: 'var(--text-secondary)' }}>
                {tier.seats * tier.perSeat}
              </Text>
            </div>
          ))}
          {onAddSeat ? (
            <UnstyledButton
              onClick={onAddSeat}
              style={{
                alignSelf: 'flex-start', background: 'none', cursor: 'pointer',
                fontSize: 'var(--text-tag)', color: 'var(--seed-base)',
                textDecoration: 'underline', marginTop: 'var(--space-1)',
              }}
            >
              {t('supportSeats.addSeat')}
            </UnstyledButton>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default SupportSeatsMeter;
